import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx-js-style"

import {
  calculateItemSalePrice,
  formatMobilePhone,
  moment,
  ObjectValues,
  thousandSeparator
} from "utils"
import { productUnitsSymbol } from "constant"
import { getOrders } from "services"
import { Button } from "components"
import classes from "./RrExcelButton.module.scss"

const limit = 30

const columnsCodes = {
  "ردیف": 0,
  "نام مشتری": 1,
  "شماره سفارش": 2,
  "تاریخ ثبت سفارش": 3,
  "تاریخ تایید سفارش": 4,
  "محصولات خریداری شده": 5,
  "گزارش وضعیت سفارش": 8,
  "اطلاعات طریق سفارش": 9,
  "تاریخ خروج": 10,
  "نام انباردار": 11
}

const border = {
  top: {
    style: "medium", color: { rgb: "CCCCCC" }
  },
  bottom: {
    style: "medium", color: { rgb: "CCCCCC" }
  },
  left: {
    style: "medium", color: { rgb: "CCCCCC" }
  },
  right: {
    style: "medium", color: { rgb: "CCCCCC" }
  }
}

const RrExcelButton = (props) => {
  const { filter } = props

  const [t] = useTranslation()

  const [sending, setSending] = useState(false)

  const sortOrders = (orders) => {
    const addedBySystemOrders = []

    const usersOrders = []

    orders.forEach(o => {
      o.addedBySystem
        ? addedBySystemOrders.push(o)
        : usersOrders.unshift(o)
    })

    const sortedAddedBySystemOrders =
      addedBySystemOrders.sort((a, b) =>
        a.addedByFullName?.localeCompare(b.addedByFullName)
      )

    return [...sortedAddedBySystemOrders, ...usersOrders]
  }

  const getItemsList = (sortedOrders) => {
    const itemsMap = {}

    sortedOrders.forEach(sortedOrder => {
      sortedOrder.items.forEach(item => {

        const value = itemsMap[item.option._id]

        const salePriceWithDiscount =
          calculateItemSalePrice(item) -
          (item.systemDiscount > 0 ? item.systemDiscount : 0) -
          item.foodDiscount

        itemsMap[item.option._id] =
          !!value
            ?
            {
              sku: item.sku,
              foodName: item.foodName,
              isSimple: item.isSimple,
              option: item.option,
              unit: item.unit,
              totalQuantity: value.totalQuantity + item.quantity,
              totalSalePrice:
                value.totalSalePrice + salePriceWithDiscount * item.quantity

            }
            :
            {
              sku: item.sku,
              foodName: item.foodName,
              isSimple: item.isSimple,
              option: item.option,
              unit: item.unit,
              totalQuantity: item.quantity,
              totalSalePrice: salePriceWithDiscount * item.quantity
            }
      })
    })

    return ObjectValues(itemsMap)
  }

  const getStatistics = (sortedOrders) => {
    const totalItemsQuantity =
      sortedOrders.reduce((t, sortedOrder) =>
        t +=
        sortedOrder.items.reduce((t, item) =>
          t += item.quantity
          , 0
        )
        , 0
      )

    const totalItemsSalePrice =
      sortedOrders.reduce((t, sortedOrder) =>
        t += sortedOrder.totalBillAmount
        , 0
      )

    return {
      totalItemsQuantity,
      totalItemsSalePrice
    }
  }

  const getAccessesList = (sortedOrders) => {
    const accessesMap = {}

    const totalUsersOrders = {
      addedByFullName: "",
      totalNumberOfOrders: 0,
      totalOrdersBillAmount: 0
    }

    sortedOrders.forEach(sortedOrder => {
      if (!sortedOrder.addedBySystem) {
        totalUsersOrders.addedByFullName = "سایت"

        totalUsersOrders.totalNumberOfOrders += 1

        totalUsersOrders.totalOrdersBillAmount += sortedOrder.totalBillAmount

        return
      }

      const value =
        accessesMap[sortedOrder.addedByAccountId]

      accessesMap[sortedOrder.addedByAccountId] =
        !!value
          ?
          {
            addedByFullName: sortedOrder.addedByFullName,
            totalNumberOfOrders: value.totalNumberOfOrders + 1,
            totalOrdersBillAmount: value.totalOrdersBillAmount + sortedOrder.totalBillAmount
          }
          :
          {
            addedByFullName: sortedOrder.addedByFullName,
            totalNumberOfOrders: 1,
            totalOrdersBillAmount: sortedOrder.totalBillAmount
          }
    })

    const result =
      ObjectValues(accessesMap)

    !!totalUsersOrders.addedByFullName &&
      result.push(totalUsersOrders)

    return result
  }

  const makeReport = (orders) => {
    setSending(true)

    const sortedOrders = sortOrders(orders)

    const itemsList = getItemsList(sortedOrders)

    const accessesList = getAccessesList(sortedOrders)

    const {
      totalItemsQuantity,
      totalItemsSalePrice
    } = getStatistics(sortedOrders)

    try {
      const ws = XLSX.utils.aoa_to_sheet([[]])

      const wb = XLSX.utils.book_new()

      wb.Workbook = { Views: [{ RTL: true }] }

      const headerRow = [
        {
          t: "s",
          v: "ردیف",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center" }
          }
        },
        {
          t: "s",
          v: "نام مشتری",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center" }
          }
        },
        {
          t: "s",
          v: "شماره سفارش",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "تاریخ ثبت سفارش",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "تاریخ تایید سفارش",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "محصولات خریداری شده",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center" }
          }
        },
        {
          v: "",
          t: "s",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center" }
          }
        },
        {
          v: "",
          t: "s",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center" }
          }
        },
        {
          t: "s",
          v: "گزارش وضعیت سفارش",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "اطلاعات طریق سفارش",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "تاریخ خروج",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        },
        {
          t: "s",
          v: "نام انباردار",
          s: {
            border,
            font: { bold: true, name: "IRNazanin" },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          }
        }
      ]

      XLSX.utils.sheet_add_aoa(
        ws,
        [headerRow],
        { origin: "A1" }
      )

      ws["!rows"] = [{ "hpx": 30 }]

      ws["!cols"] = [
        { "wpx": 40 },
        { "wpx": 110 },
        { "wpx": 70 },
        { "wpx": 70 },
        { "wpx": 70 },

        { "wpx": 60 },
        { "wpx": 200 },
        { "wpx": 60 },

        { "wpx": 110 },
        { "wpx": 110 },
        { "wpx": 110 },
        { "wpx": 110 }
      ]

      ws["!merges"] = [{
        s: { c: columnsCodes["محصولات خریداری شده"], r: 0 },
        e: { c: columnsCodes["محصولات خریداری شده"] + 2, r: 0 }
      }]

      let rowIndex = 1

      sortedOrders.forEach((sortedOrder, orderIndex) => {
        const endRow =
          rowIndex + sortedOrder.items.length - 1

        ws["!merges"] = [
          ...ws["!merges"],
          {
            s: { c: columnsCodes["ردیف"], r: rowIndex },
            e: { c: columnsCodes["ردیف"], r: endRow }
          },
          {
            s: { c: columnsCodes["نام مشتری"], r: rowIndex },
            e: { c: columnsCodes["نام مشتری"], r: endRow }
          },
          {
            s: { c: columnsCodes["شماره سفارش"], r: rowIndex },
            e: { c: columnsCodes["شماره سفارش"], r: endRow }
          },
          {
            s: { c: columnsCodes["تاریخ ثبت سفارش"], r: rowIndex },
            e: { c: columnsCodes["تاریخ ثبت سفارش"], r: endRow }
          },
          {
            s: { c: columnsCodes["تاریخ تایید سفارش"], r: rowIndex },
            e: { c: columnsCodes["تاریخ تایید سفارش"], r: endRow }
          },
          {
            s: { c: columnsCodes["گزارش وضعیت سفارش"], r: rowIndex },
            e: { c: columnsCodes["گزارش وضعیت سفارش"], r: endRow }
          },
          {
            s: { c: columnsCodes["اطلاعات طریق سفارش"], r: rowIndex },
            e: { c: columnsCodes["اطلاعات طریق سفارش"], r: endRow }
          },
          {
            s: { c: columnsCodes["تاریخ خروج"], r: rowIndex },
            e: { c: columnsCodes["تاریخ خروج"], r: endRow }
          },
          {
            s: { c: columnsCodes["نام انباردار"], r: rowIndex },
            e: { c: columnsCodes["نام انباردار"], r: endRow }
          }
        ]

        sortedOrder.items.forEach((item, itemIndex) => {
          ws["!rows"][rowIndex + itemIndex] = { hpx: 35 }

          XLSX.utils.sheet_add_aoa(
            ws,
            [[
              {
                t: "n",
                v: orderIndex + 1,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              },
              {
                t: "s",
                v: `${sortedOrder.customerFullName}\n${formatMobilePhone(sortedOrder.customerMobilePhone)}`,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: sortedOrder.code,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              },
              {
                t: "s",
                v: `${moment(sortedOrder.date).format("jYYYY/jMM/jDD")}\n${moment(sortedOrder.date).format("HH:mm")}`,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: `${moment(sortedOrder.paymentDate).format("jYYYY/jMM/jDD")}\n${moment(sortedOrder.paymentDate).format("HH:mm")}`,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: `${item.quantity}${productUnitsSymbol[item.unit]}`,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              },
              {
                t: "s",
                v: `${item.foodName}${item.isSimple ? "" : ` - ${item.option.name}`}${item.sku ? ` - ${item.sku}` : ""}`,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "n",
                v: item.foodCode,
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              },
              {
                t: "s",
                v: sortedOrder.isDispatched ? "خروج از انبار\nپرداخت شده" : "خروج نخورده\nپرداخت شده",
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: sortedOrder.addedBySystem ? sortedOrder.addedByFullName : "سایت",
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: sortedOrder.isDispatched ? `${moment(sortedOrder.dispatchedAt).format("jYYYY/jMM/jDD")}\n${moment(sortedOrder.dispatchedAt).format("HH:mm")}` : "",
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },
              {
                t: "s",
                v: sortedOrder.isDispatched ? sortedOrder.dispatchedByFullName : " ",
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              }
            ]],
            {
              origin: {
                c: columnsCodes["ردیف"],
                r: rowIndex + itemIndex
              }
            }
          )
        })

        rowIndex += sortedOrder.items.length
      })

      const offset = 4

      rowIndex += offset

      XLSX.utils.sheet_add_aoa(
        ws,
        [[
          {
            t: "s",
            v: "نام محصول",
            s: {
              border,
              font: { name: "IRNazanin", bold: true },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            v: "",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center" }
            }
          },
          {
            v: "",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center" }
            }
          },
          {
            t: "s",
            v: "تعداد محصول",
            s: {
              border,
              font: { name: "IRNazanin", bold: true },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            t: "s",
            v: "میانگین فروش (تومان)",
            s: {
              border,
              font: { name: "IRNazanin", bold: true },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            v: ""
          },
          {
            v: ""
          },
          {
            v: ""
          },
          {
            t: "s",
            v: "طریق سفارش",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            v: "تعداد کل سفارشات",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            v: "جمع مبلغ کل سفارشات (تومان)",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          }
        ]],
        {
          origin: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          }
        }
      )

      ws["!merges"] = [
        ...ws["!merges"],
        {
          s: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          },
          e: {
            c: columnsCodes["شماره سفارش"],
            r: rowIndex
          }
        }
      ]

      const arrayLength =
        Math.max(itemsList.length, accessesList.length)

      Array.from(Array(arrayLength).keys()).forEach((_, index) => {
        rowIndex += 1

        ws["!rows"][rowIndex] = { hpx: 35 }

        const access = accessesList[index]

        const item = itemsList[index]

        let aoa = []

        if (item) {
          aoa = [
            {
              t: "s",
              v: `${item.foodName}${item.isSimple ? "" : ` - ${item.option.name}`}${item.sku ? ` - ${item.sku}` : ""}`,
              s: {
                border,
                font: { name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            },
            {
              v: "",
              s: {
                border,
                font: { bold: true, name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center" }
              }
            },
            {
              v: "",
              s: {
                border,
                font: { bold: true, name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center" }
              }
            },
            {
              t: "s",
              v: `${item.totalQuantity}${productUnitsSymbol[item.unit]}`,
              s: {
                border,
                font: { name: "IRNazanin", bold: true },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            },
            {
              t: "s",
              v: thousandSeparator(item.totalSalePrice / item.totalSalePrice, ""),
              s: {
                border,
                font: { name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            }]
        } else {
          aoa = [
            {
              t: "s",
              v: "",
              s: {
                border,
                font: { name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            },
            {
              v: "",
              s: {
                border,
                font: { bold: true, name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center" }
              }
            },
            {
              v: "",
              s: {
                border,
                font: { bold: true, name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center" }
              }
            },
            {
              t: "s",
              v: "",
              s: {
                border,
                font: { name: "IRNazanin", bold: true },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            },
            {
              t: "s",
              v: "",
              s: {
                border,
                font: { name: "IRNazanin" },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              }
            }
          ]
        }

        if (access) {
          console.log(access)

          aoa.push(
            ...[
              {
                v: ""
              },
              {
                v: ""
              },
              {
                v: ""
              },
              {
                t: "s",
                v: access.addedByFullName || " ",
                s: {
                  border,
                  font: { name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center", wrapText: true }
                }
              },

              {
                t: "n",
                v: access.totalNumberOfOrders,
                s: {
                  border,
                  font: { bold: true, name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              },
              {
                t: "s",
                v: thousandSeparator(access.totalOrdersBillAmount, ""),
                s: {
                  border,
                  font: { bold: true, name: "IRNazanin" },
                  alignment: { horizontal: "center", vertical: "center" }
                }
              }
            ]
          )
        } else {
          aoa.push(
            ...[
              {
                v: ""
              },
              {
                v: ""
              },
              {
                v: ""
              },
              {
                t: "s",
                v: ""
              },

              {
                t: "s",
                v: ""
              },
              {
                t: "s",
                v: ""
              }
            ]
          )
        }

        XLSX.utils.sheet_add_aoa(
          ws,
          [aoa],
          {
            origin: {
              c: columnsCodes["ردیف"],
              r: rowIndex
            }
          }
        )

        ws["!merges"] = [
          ...ws["!merges"],
          {
            s: {
              c: columnsCodes["ردیف"],
              r: rowIndex
            },
            e: {
              c: columnsCodes["شماره سفارش"],
              r: rowIndex
            }
          }
        ]
      })

      rowIndex += offset

      XLSX.utils.sheet_add_aoa(
        ws,
        [[
          {
            t: "s",
            v: "تعداد کل اجناس",
            s: {
              border,
              font: { name: "IRNazanin", bold: true },
              alignment: { vertical: "center", wrapText: true }
            }
          },
          {
            v: "",
            s: {
              border,
              font: { bold: true, name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center" }
            }
          },
          {
            t: "n",
            v: totalItemsQuantity,
            s: {
              border,
              font: { name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          }
        ]],
        {
          origin: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          }
        }
      )

      ws["!merges"] = [
        ...ws["!merges"],
        {
          s: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          },
          e: {
            c: columnsCodes["نام مشتری"],
            r: rowIndex
          }
        }
      ]

      rowIndex++

      rowIndex++

      XLSX.utils.sheet_add_aoa(
        ws,
        [[
          {
            t: "s",
            v: "مبلغ کل (تومان)",
            s: {
              border,
              font: { name: "IRNazanin", bold: true },
              alignment: { vertical: "center", wrapText: true }
            }
          },
          {
            v: "",
            s: {
              border,
              font: { name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          },
          {
            t: "s",
            v: thousandSeparator(totalItemsSalePrice, ""),
            s: {
              border,
              font: { name: "IRNazanin" },
              alignment: { horizontal: "center", vertical: "center", wrapText: true }
            }
          }
        ]],
        {
          origin: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          }
        }
      )

      ws["!merges"] = [
        ...ws["!merges"],
        {
          s: {
            c: columnsCodes["ردیف"],
            r: rowIndex
          },
          e: {
            c: columnsCodes["نام مشتری"],
            r: rowIndex
          }
        }
      ]

      XLSX.utils.book_append_sheet(wb, ws, "Sheet1")

      XLSX.writeFile(wb, `گزارش انبار (${moment().format("jYYYY-jMM-jDD HH-mm")}).xlsx`)
    } catch (err) {
      console.log(err)
    }
  }

  const handleExcel = async () => {
    setSending(true)

    let skip = 0

    const orders = []

    try {
      while (1) {
        const response =
          await getOrders({
            skip,
            limit,
            isDispatched: filter.isDispatched,
            branchId: filter.branchId,
            fromDate: filter.fromDate,
            toDate: filter.toDate,
            fromDispatchedAt: filter.fromDispatchedAt,
            toDispatchedAt: filter.toDispatchedAt,
            fromDeliveredAt: filter.fromDeliveredAt,
            toDeliveredAt: filter.toDeliveredAt,
            statusMultiple: ["delivered"],
            accountId: filter.account?.value,
            addedByAccountId: filter.dispatchedByAccount?.value
          })

        orders.push(...response)

        if (response.length !== limit) break
        else skip += limit
      }

      makeReport(orders)
    } catch (err) {
      console.log(err)
    }

    setSending(false)
  }

  return (
    <div className={classes["footer"]}>
      <Button
        loading={sending}
        color="success"
        onClick={handleExcel}
      >
        {t("تهیه_گزارش")}
      </Button>
    </div>
  )
}

export default RrExcelButton